import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { Text } from "../../components/Text";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

import { LoginDto } from "../../model/user/dto/LoginDto";
import { useAppDispatch } from "../../redux/store";
import { userActions } from "../../redux/user/user.slice";
import { Spacing } from "../../theme/Spacing";
import { AtiraToast } from "../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../utils/onSetTypedCRUDErrors";
import { RegisterWrapper } from "./RegisterWrapper";
import { Input } from "../../components/Input";
import { Regexes } from "../../utils/String";
import isEmpty from "lodash/isEmpty";
import { TestIDs } from "../../tests/types/testIds";
import { LoginWithGoogleReturnPath } from "../../model/user/types/LoginWithGoogleReturnPath.enum";
import { GoogleButton } from "../../components/logins/GoogleButton";
import { Login2faRequiredDto } from "../../model/user/dto/Login2faRequiredDto";
import { isTwoFactorRequired } from "../../model/user/dto/LoginResult";
import { LoginTwoFactor } from "./LoginTwoFactor";

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;

  &:hover {
    background-color: ${(props) => props.theme.lightMain};
  }
`;

const StyledGoogleButton = styled(GoogleButton)`
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  height: 2.3rem;
  width: 100%;
  border: 1px solid ${(props) => props.theme.transparent};
  transition: all 0.2s;

  &:hover {
    border: 1px solid #c6c7c8;
    background-color: ${(props) => props.theme.darkSub};
  }
`;

const PasswordButton = styled(Button)`
  background-color: ${(props) => props.theme.transparent};
  color: ${(props) => props.theme.main};
  font-size: 0.8rem;
  margin: 0;
  padding: 0;
`;

const StyledInput = styled(Input)`
  width: 23rem;
  height: 2.3rem;
  border: 1px solid ${(props) => props.theme.lightergray};
  padding: 0.5rem;
  transition: border 0.2s;

  &:focus {
    border: 1px solid #86b7fe !important;
  }
`;

const StyledHr = styled.hr`
  background-color: ${(props) => props.theme.darkerSub};
  height: 1px;
  border: 0;
  opacity: 1;
  width: 100%;
`;

const TogglePageButton = styled(Button)`
  background-color: ${(props) => props.theme.transparent};
  margin: 0;
  color: ${(props) => props.theme.main};
  padding: 0;
`;

const ResponsiveFlex = styled(Flex)`
  @media (min-width: 360px) and (max-width: 990px) {
    padding: ${Spacing.m};
  }
`;

export const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState<Login2faRequiredDto | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();
  useDocumentTitle(`${t("app.name").toUpperCase()} - ${t("common.login")}`);

  const { control, handleSubmit, getValues, setError } = useForm<LoginDto>({
    defaultValues: { email: "", password: "" },
  });

  const goToApp = () => {
    const redirectPath = searchParams.get("redirect");

    navigate(redirectPath ? `/${redirectPath}` : "/dashboard", {
      replace: true,
    });
  };

  const onNext = async () => {
    try {
      setLoading(true);
      const result = await dispatch(userActions.login(getValues())).unwrap();

      if (isTwoFactorRequired(result)) {
        setChallenge(result);
        return;
      }

      goToApp();
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      onSetTypedCRUDErrors<LoginDto>(e, setError);
    } finally {
      setLoading(false);
    }
  };

  if (challenge) {
    return (
      <RegisterWrapper>
        <ResponsiveFlex
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          flex={1}
        >
          <LoginTwoFactor
            challengeToken={challenge.challengeToken}
            methods={challenge.methods}
            onVerified={goToApp}
            onBack={() => setChallenge(null)}
          />
        </ResponsiveFlex>
      </RegisterWrapper>
    );
  }

  return (
    <RegisterWrapper>
      <ResponsiveFlex
        width={"100%"}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap="s"
      >
        <Controller
          name="email"
          rules={{ required: true, pattern: Regexes.EMAIL_REGEX }}
          control={control}
          render={({ field: { value, onChange }, formState: { errors } }) => (
            <StyledInput
              title={t("common.email")}
              type="email"
              value={value}
              onChange={onChange}
              placeholder={t("common.email")}
              id="email"
              required
              valid={isEmpty(errors.email)}
              errorMessage={
                (errors.email?.message as string) || t("signup.error.email")
              }
              dataTestId={TestIDs.auth.login.email}
            />
          )}
        />

        <Flex flexDirection="column" width={"100%"} gap="s">
          <Controller
            name="password"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <StyledInput
                title={t("common.password")}
                value={value}
                type="password"
                onChange={onChange}
                required
                placeholder={t("common.password")}
                id="password"
                valid={isEmpty(errors.password)}
                errorMessage={
                  (errors.password?.message as string) ||
                  t("signup.error.password")
                }
                dataTestId={TestIDs.auth.login.password}
              />
            )}
          />

          <Flex justifyContent="space-between">
            <PasswordButton
              onClick={() => navigate("/forgot-password")}
              title={t("login.forgot_password")}
            />
          </Flex>
        </Flex>

        <Flex flexDirection="column" gap="m" width={"100%"} marginTop="m">
          <StyledButton
            loading={loading}
            onClick={handleSubmit(onNext)}
            title={t("common.login")}
            dataTestId={TestIDs.auth.login.submit}
          />

          <StyledHr />
          <StyledGoogleButton returnPath={LoginWithGoogleReturnPath.LOGIN}>
            {t("sign_in_with_google")}
          </StyledGoogleButton>
          <StyledHr />
        </Flex>
      </ResponsiveFlex>

      <ResponsiveFlex gap="s" alignItems="center">
        <Text fontSize="s" lineHeight={"normal"}>
          {t("login.do_not_have_account")}
        </Text>
        <TogglePageButton
          onClick={() => navigate(`/signup${search}`)}
          title={t("common.signup")}
        />
      </ResponsiveFlex>
    </RegisterWrapper>
  );
};
